// Rasterizes SVGs client-side so active SVG content never reaches the media
// origin (the server rejects SVG). <img> does not execute SVG scripts.

const MAX_SIDE = 2048;
const FALLBACK_SIZE = 1024;

type Size = { width: number; height: number };

export function isSvgFile(file: File): boolean {
  return file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
}

export async function svgFileToPng(file: File): Promise<File> {
  const intrinsicSize = getSvgIntrinsicSize(await file.text());

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);

    const base = intrinsicSize ?? {
      width: img.naturalWidth || FALLBACK_SIZE,
      height: img.naturalHeight || FALLBACK_SIZE,
    };
    const { width, height } = scaleToLongestSide(base, MAX_SIDE);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not get canvas context for SVG conversion");
    }
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await canvasToPngBlob(canvas);
    const pngName = file.name.replace(/\.svg$/i, "") + ".png";
    return new File([blob], pngName, { type: "image/png" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load SVG for conversion"));
    img.src = url;
  });
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Failed to rasterize SVG to PNG"));
      }
    }, "image/png");
  });
}

function scaleToLongestSide(size: Size, longestSide: number): Size {
  const longest = Math.max(size.width, size.height);
  const scale = longest > 0 ? longestSide / longest : 1;
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  };
}

function getSvgIntrinsicSize(svgText: string): Size | null {
  const svg = new DOMParser().parseFromString(
    svgText,
    "image/svg+xml",
  ).documentElement;
  if (svg.nodeName.toLowerCase() !== "svg") {
    return null;
  }

  const width = parseLength(svg.getAttribute("width"));
  const height = parseLength(svg.getAttribute("height"));
  if (width && height) {
    return { width, height };
  }

  const viewBox = svg
    .getAttribute("viewBox")
    ?.split(/[\s,]+/)
    .map(Number);
  const [, , vbWidth, vbHeight] = viewBox ?? [];
  if (vbWidth && vbHeight && vbWidth > 0 && vbHeight > 0) {
    return { width: vbWidth, height: vbHeight };
  }
  return null;
}

function parseLength(value: string | null): number | null {
  if (!value || value.includes("%")) {
    return null;
  }
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}
